import { FC, memo, useState, useMemo } from 'react';
import { Modal, Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { modalBoxStyle, modalContainerStyle } from '@utils';

import { T_KEYS } from '../../constants';
import { ChannelType, IMsoolUnit, ILinkedUnit } from '../../types';
import { ChannelLogo } from '../ChannelLogos';
import type { ChannelLogoType } from '../ChannelLogos';
import { BackgroundCompressed } from '../../../../utils/image-consts';

interface ChannelCardConfig {
  type: ChannelType;
  logoChannel: ChannelLogoType;
  label: string;
}

const CHANNEL_CARDS: ChannelCardConfig[] = [
  { type: ChannelType.Airbnb, logoChannel: 'airbnb', label: 'Airbnb' },
  { type: ChannelType.Booking, logoChannel: 'booking', label: 'Booking' },
  { type: ChannelType.Gathern, logoChannel: 'gathern', label: 'Gathern' },
];

export interface SelectChannelModalProps {
  open: boolean;
  onClose: () => void;
  onSelectChannel: (channelType: ChannelType) => void;
  selectedProperty: IMsoolUnit | null;
  unmergedCounts: {
    airbnb: number;
    booking: number;
    gathern: number;
  };
}

const SelectChannelModal: FC<SelectChannelModalProps> = memo(({
  open,
  onClose,
  onSelectChannel,
  selectedProperty,
  unmergedCounts,
}) => {
  const { t } = useTranslation();
  const [selectedChannel, setSelectedChannel] = useState<ChannelType | null>(null);

  // Get channel connection status from selectedProperty.channels
  const channelStatus = useMemo(() => {
    const channels = selectedProperty?.channels ?? [];
    
    const getChannelInfo = (channelType: ChannelType): ILinkedUnit | undefined => {
      return channels.find(c => c.channelType === channelType);
    };

    const airbnbChannel = getChannelInfo(ChannelType.Airbnb);
    const bookingChannel = getChannelInfo(ChannelType.Booking);
    const gathernChannel = getChannelInfo(ChannelType.Gathern);

    return {
      [ChannelType.Airbnb]: airbnbChannel?.status === true,
      [ChannelType.Booking]: bookingChannel?.status === true,
      [ChannelType.Gathern]: gathernChannel?.status === true,
    } as Record<ChannelType, boolean>;
  }, [selectedProperty]);

  const unmergedCountMap = {
    [ChannelType.Airbnb]: unmergedCounts.airbnb,
    [ChannelType.Booking]: unmergedCounts.booking,
    [ChannelType.Gathern]: unmergedCounts.gathern,
  } as Record<ChannelType, number>;

  const handleChannelClick = (channelType: ChannelType) => {
    const isConnected = channelStatus[channelType];
    const hasUnits = unmergedCountMap[channelType] > 0;
    if (isConnected || !hasUnits) return;
    setSelectedChannel(channelType);
  };

  const handleLink = () => {
    if (selectedChannel) {
      onSelectChannel(selectedChannel);
      setSelectedChannel(null);
    }
  };

  const handleClose = () => {
    setSelectedChannel(null);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={modalContainerStyle}
      aria-labelledby="select-channel-modal"
    >
      <Box
        sx={{
          ...modalBoxStyle,
          p: 0,
          display: 'grid',
          flexDirection: 'unset',
          width: { xs: '800px', lg: '1000px' },
          height: '90vh',
        }}
        className="w-[800px] lg:w-[1000px] h-[90vh] grid grid-cols-11 relative overflow-hidden"
      >

        {/* Start Side - Purple Background */}
        <div
          className="col-span-5 h-full px-6 py-12 flex flex-col justify-center gap-4 bg-primary bg-no-repeat bg-center bg-cover"
          style={{ backgroundImage: `url(${BackgroundCompressed})` }}
        >
          <h2 className="text-white text-2xl lg:text-[32px] font-semibold">
            {t(T_KEYS.SELECT_CHANNEL_TITLE)}
          </h2>
          <p className="text-white text-sm lg:text-base">
            {t(T_KEYS.SELECT_CHANNEL_DESCRIPTION)}
          </p>
        </div>
        {/* End Side - Content */}
        <div className="col-span-6 relative p-4 overflow-y-auto flex gap-4 pt-4 flex-col scrollbar-thin scrollbar-thumb-primary scrollbar-track-gray-100">
          <div className="flex-1 pt-4 pb-4 flex flex-col">
            <div className="w-full flex justify-between items-center">
              <h3 className="text-[#0F172A] text-xl">
                {t(T_KEYS.CONNECT_NEW_CHANNEL)}
              </h3>
              {/* Close Button */}
              <button
                aria-label="close"
                onClick={handleClose}
                className="text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full p-2 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            </div>
                <p className="text-[#8E98A1] text-base">
                  {t(T_KEYS.SELECT_CHANNEL_DESCRIPTION)}
                </p>


            {/* Channel Cards Grid */}
            <div className="pt-6 grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8 border-b border-[#EAEDF1] pb-6">
              {CHANNEL_CARDS.map(({ type, logoChannel, label }) => {
                const isConnected = channelStatus[type];
                const hasUnits = unmergedCountMap[type] > 0;
                const isSelected = selectedChannel === type;
                const isNoUnits = !isConnected && !hasUnits;
                const isClickable = !isConnected && hasUnits;

                const cardBg = isSelected
                  ? 'border-[#5b2ff9] bg-[rgba(91,47,249,0.1)]'
                  : isNoUnits
                    ? 'border-[#e5e7eb] bg-[#f8f8f8]'
                    : 'border-[#e5e7eb] bg-white';

                return (
                  <div
                    key={type}
                    onClick={() => handleChannelClick(type)}
                    className={`
                      h-[166px] border border-solid rounded-[20px] flex flex-col items-center justify-center gap-4 px-4 py-5 transition-colors
                      ${cardBg}
                      ${isNoUnits ? 'opacity-50 cursor-not-allowed' : ''}
                      ${isClickable ? 'cursor-pointer' : ''}
                      ${isConnected ? 'cursor-default' : ''}
                    `}
                  >
                    <ChannelLogo channel={logoChannel} size="lg" />
                    <div className="flex flex-col items-center gap-2">
                      <span className="text-[#262626] text-base font-semibold leading-6">{label}</span>
                      {isConnected ? (
                        <div className="flex items-center gap-1">
                          <span className="text-[12px] text-[#666] leading-4">
                            {t(T_KEYS.ALREADY_CONNECTED)}
                          </span>
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <circle cx="8" cy="8" r="8" fill="#5B3FFF" />
                            <path d="M11.5 5.5L6.5 10.5L4 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                      ) : (
                        <span className="text-[12px] text-[#666] leading-4 text-center">
                          {hasUnits
                            ? t(T_KEYS.UNITS_TO_LINK, { count: unmergedCountMap[type] })
                            : t(T_KEYS.NO_UNITS_TO_LINK)
                          }
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Link Button */}
            <div className="mt-auto flex gap-8 items-center">
              <Button
                size="small"
                variant="contained"
                onClick={handleLink}
                disabled={!selectedChannel}
              >
                {t(T_KEYS.LINK_UNIT)}
              </Button>
              <span className="text-[#8E98A1]">
                {t(T_KEYS.SELECT_CHANNEL_DESCRIPTION)}
              </span>
            </div>
          </div>
        </div>

      </Box>
    </Modal>
  );
});

SelectChannelModal.displayName = 'SelectChannelModal';

export default SelectChannelModal;
